import { Switch } from 'components/ui/switch';
import { ReactNode } from 'react';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { SECTION_CARD, SECTION_HEADER, SECTION_TITLE } from './SectionCard';

/** The grey bar of totals over an item table's rows. */
export const ITEM_TOTALS_BAR = 'border-divider bg-grey-50 flex min-h-[38px] items-center border-b px-4 py-2';

/**
 * The timecard and expense tables' box. It never collapses, and its title row stays pinned while the pane
 * scrolls: the negative `top` is the pane's padding at each container width, plus the radius.
 */
export const ItemTableCard = ({
    title,
    actions,
    children,
}: {
    title: ReactNode;
    actions: ReactNode;
    children: ReactNode;
}) => (
    <div className={SECTION_CARD}>
        <div
            className={cn(
                SECTION_HEADER,
                'bg-paper sticky top-[-30px] z-1 rounded-lg @max-5xl:top-[-22px] @max-3xl:top-[-16px]'
            )}
        >
            {/* The title and the actions wrap as whole units when the pane gets narrow (e.g. the Summary
                drawer is open) instead of breaking their own content. `min-w-0` keeps the row at the card's width
                when the title alone is wider, so the wrapped actions still end at the card's edge. */}
            <div className="my-2.5 ml-2 flex min-w-0 grow flex-wrap items-center justify-between gap-y-1">
                {title}
                <div className="ml-auto flex flex-wrap items-center justify-end gap-x-2 gap-y-1">{actions}</div>
            </div>
        </div>
        <div className="border-divider border-t">{children}</div>
    </div>
);

/** The switch, labelled on its left, that adds the advanced-billing column to an item table. */
export const AdvancedBillingSwitch = ({
    checked,
    onCheckedChange,
    disabled,
}: {
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    disabled: boolean;
}) => (
    <label className={cn('flex items-center gap-3.5 whitespace-nowrap', !disabled && 'cursor-pointer')}>
        <span className={cn(SECTION_TITLE, disabled && 'text-fg-disabled')}>
            {t('invoices.details.table.advanced-billing')}
        </span>
        <Switch
            checked={checked}
            onCheckedChange={onCheckedChange}
            disabled={disabled}
            className="data-unchecked:bg-grey-500/38"
        />
    </label>
);
